/**
 * CardEditModal Component
 *
 * Modal for editing dashboard cards using natural language (powered by Claude Haiku)
 * or manual controls.
 *
 * Features:
 * - Natural language input for card configuration
 * - AI recommendations from Haiku
 * - Manual override controls
 * - Live preview of changes
 * - Apply/Cancel actions
 */

import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { configureCardWithAI } from '../../services/cardConfigService';
import BigTextBlock from '../Charts/BigTextBlock';
import LineChart from '../Charts/LineChart';
import BarChart from '../Charts/BarChart';
import LineBarChart from '../Charts/LineBarChart';
import DualLineChart from '../Charts/DualLineChart';
import TripleLineChart from '../Charts/TripleLineChart';
import './CardEditModal.css';

const CardEditModal = ({ cardId, currentChart, dashboardId, onClose, onApply }) => {
  // Natural language input
  const [prompt, setPrompt] = useState('');
  const [loading, setLoading] = useState(false);

  // AI recommendation
  const [recommendation, setRecommendation] = useState(null);
  const [chartDefinition, setChartDefinition] = useState(null);
  const [error, setError] = useState(null);

  // Manual overrides
  const [manualMetric, setManualMetric] = useState('');
  const [manualChartType, setManualChartType] = useState('');
  const [manualPeriod, setManualPeriod] = useState('');
  const [manualStartDate, setManualStartDate] = useState('');
  const [manualYLabel, setManualYLabel] = useState('');

  // Preview state
  const [previewChartId, setPreviewChartId] = useState(null);
  const [useManualConfig, setUseManualConfig] = useState(false);

  // Extract current config
  const currentChartData = currentChart?.chart || currentChart;
  const currentMetric = currentChartData?.metrics?.[0]?.metric;
  const currentMetricId = currentMetric?.metric_id || '';
  const currentSecondMetricId = currentMetric?.second_metric_id || null;
  const currentChartType = currentChartData?.type || 'big_text_block';
  const currentPeriod = currentMetric?.interval_period || 'Monthly';
  const currentDimension = currentMetric?.dimension_column || null;

  // Initialize manual controls with current config
  useEffect(() => {
    setManualMetric(currentMetricId);
    setManualChartType(currentChartType);
    setManualPeriod(currentPeriod);
    setManualStartDate(currentMetric?.starts_from_monthly || '');
    setManualYLabel(currentMetric?.y_label || '');
  }, [currentMetricId, currentChartType, currentPeriod, currentMetric]);

  // Handle natural language generation
  const handleGenerateConfig = async () => {
    if (!prompt.trim() || prompt.trim().length < 3) {
      setError('Please enter at least 3 characters');
      return;
    }

    setLoading(true);
    setError(null);
    setUseManualConfig(false);

    try {
      const currentSecondMetricId = currentMetric?.second_metric_id || null;
      const response = await configureCardWithAI(prompt, dashboardId, cardId, {
        metric_id: currentMetricId,
        ...(currentSecondMetricId && { second_metric_id: currentSecondMetricId }),
        chart_type: currentChartType,
        interval_period: currentPeriod
      });

      if (response.success) {
        setRecommendation(response.recommendation);
        setChartDefinition(response.chart_definition);

        // Update manual controls with AI recommendation
        setManualMetric(response.recommendation.metric_id);
        setManualChartType(response.recommendation.chart_type);
        setManualPeriod(response.recommendation.interval_period);
        setManualStartDate(response.recommendation.starts_from_monthly || '');

        // Set preview chart ID
        if (response.chart_definition?.chart?.id) {
          setPreviewChartId(response.chart_definition.chart.id);
        }
      } else {
        setError(response.error || 'Failed to generate configuration');
      }
    } catch (err) {
      console.error('Error generating config:', err);
      setError(err.message || 'Failed to generate configuration. Please try manual controls.');
    } finally {
      setLoading(false);
    }
  };

  // Handle manual control changes
  const handleManualChange = (field, value) => {
    setUseManualConfig(true);

    switch (field) {
      case 'metric':
        setManualMetric(value);
        break;
      case 'chartType':
        setManualChartType(value);
        break;
      case 'period':
        setManualPeriod(value);
        break;
      case 'startDate':
        setManualStartDate(value);
        break;
      case 'yLabel':
        setManualYLabel(value);
        break;
      default:
        break;
    }
  };

  // Reset to AI recommendation
  const handleResetToAI = () => {
    if (recommendation) {
      setManualMetric(recommendation.metric_id);
      setManualChartType(recommendation.chart_type);
      setManualPeriod(recommendation.interval_period);
      setManualStartDate(recommendation.starts_from_monthly || '');
      setManualYLabel(recommendation.y_label || '');
      setUseManualConfig(false);
    }
  };

  // Handle apply
  const handleApplyClick = () => {
    // Validate
    if (!manualMetric) {
      setError('Please select a metric');
      return;
    }

    if (!manualChartType) {
      setError('Please select a chart type');
      return;
    }

    // Build chart definition from manual settings if user made changes
    let finalChartDefinition = chartDefinition;

    if (useManualConfig && recommendation) {
      // User made manual changes - rebuild chart definition
      finalChartDefinition = {
        chart: {
          ...chartDefinition.chart,
          type: manualChartType,
          metrics: [
            {
              metric: {
                metric_id: manualMetric,
                interval_period: manualPeriod,
                starts_from_monthly: manualStartDate || undefined,
                y_label: manualYLabel || undefined
              }
            }
          ]
        }
      };
    }

    if (finalChartDefinition) {
      onApply(finalChartDefinition);
    } else {
      setError('No configuration to apply. Please generate a configuration first.');
    }
  };

  // Render preview
  const renderPreview = () => {
    if (!previewChartId && !useManualConfig) {
      return (
        <div className="preview-placeholder">
          <p>No preview available. Generate a configuration to see preview.</p>
        </div>
      );
    }

    // Use preview chart ID if available (from AI), otherwise use current chart
    const chartIdToRender = previewChartId || currentChartData.id;
    const chartTypeToRender = useManualConfig ? manualChartType : (chartDefinition?.chart?.type || currentChartType);

    // Normalize chart type
    const normalizedType = chartTypeToRender.replace('_chart', '').replace('_', '');

    try {
      switch (normalizedType) {
        case 'bigtextblock':
        case 'bigtext':
          return <BigTextBlock chartId={chartIdToRender} />;
        case 'line':
          return <LineChart chartId={chartIdToRender} />;
        case 'bar':
          return <BarChart chartId={chartIdToRender} />;
        case 'linebar':
          return <LineBarChart chartId={chartIdToRender} />;
        case 'dualline':
          return <DualLineChart chartId={chartIdToRender} />;
        case 'tripleline':
          return <TripleLineChart chartId={chartIdToRender} />;
        default:
          return <p>Preview not available for chart type: {chartTypeToRender}</p>;
      }
    } catch (err) {
      console.error('Preview render error:', err);
      return <p className="preview-error">Error rendering preview</p>;
    }
  };

  // Get confidence color
  const getConfidenceColor = (confidence) => {
    if (confidence >= 0.7) return 'green';
    if (confidence >= 0.5) return 'yellow';
    return 'red';
  };

  // Get chart type display name
  const getChartTypeDisplayName = (type) => {
    const typeMap = {
      'big_text_block': 'Big Text Block',
      'line_chart': 'Line Chart',
      'bar_chart': 'Bar Chart',
      'line_bar_chart': 'Line + Bar Chart',
      'dual_line_chart': 'Dual Line Chart',
      'triple_line_chart': 'Triple Line Chart',
      'growth_chart': 'Growth Chart',
      'table': 'Table',
      'pie_chart': 'Pie Chart',
      'area_chart': 'Area Chart',
      'scatter_plot': 'Scatter Plot'
    };
    return typeMap[type] || type;
  };

  return (
    <div className="card-edit-modal-overlay" onClick={onClose}>
      <div className="card-edit-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="modal-header">
          <h2>Edit Card: {currentChartData?.name || 'Unnamed Card'}</h2>
          <button className="close-button" onClick={onClose}>✕</button>
        </div>

        {/* Current Configuration */}
        <div className="current-config">
          <h3>Current Configuration</h3>
          <div className="config-display">
            {currentMetricId}{currentSecondMetricId ? ` vs ${currentSecondMetricId}` : ''} • {getChartTypeDisplayName(currentChartType)} • {currentPeriod}{currentDimension ? ` • by ${currentDimension}` : ''}
          </div>
        </div>

        {/* Natural Language Input */}
        <div className="natural-language-section">
          <label htmlFor="prompt-input">Describe what you want to see:</label>
          <textarea
            id="prompt-input"
            className="prompt-input"
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder="e.g., Show weekly revenue for last 90 days"
            rows={3}
            maxLength={200}
            disabled={loading}
          />
          <div className="char-count">{prompt.length} / 200 characters</div>

          <button
            className="generate-button"
            onClick={handleGenerateConfig}
            disabled={loading || prompt.trim().length < 3}
          >
            {loading ? 'Generating...' : 'Generate Configuration'}
          </button>
        </div>

        {/* Error Display */}
        {error && (
          <div className="error-banner">
            ❌ {error}
          </div>
        )}

        {/* AI Recommendation */}
        {recommendation && (
          <div className="recommendation-section">
            <div className="recommendation-header">
              <h3>AI Recommendation</h3>
              <span className={`confidence-badge ${getConfidenceColor(recommendation.confidence)}`}>
                ✓ {Math.round(recommendation.confidence * 100)}%
              </span>
            </div>

            {recommendation.confidence < 0.5 && (
              <div className="warning-banner">
                ⚠️ Low confidence ({Math.round(recommendation.confidence * 100)}%). Please review or adjust manually.
              </div>
            )}

            <div className="recommendation-details">
              <div className="detail-row">
                <strong>Metric:</strong> {recommendation.metric_name || recommendation.metric_id}
              </div>
              <div className="detail-row">
                <strong>Chart Type:</strong> {getChartTypeDisplayName(recommendation.chart_type)}
              </div>
              <div className="detail-row">
                <strong>Period:</strong> {recommendation.interval_period}
                {recommendation.starts_from_monthly && ` (from ${recommendation.starts_from_monthly})`}
              </div>
              {recommendation.reasoning && (
                <div className="detail-row reasoning">
                  <strong>Reasoning:</strong> {recommendation.reasoning}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Manual Override Controls */}
        {recommendation && (
          <div className="manual-controls-section">
            <h3>Manual Adjustments (Optional)</h3>
            <div className="controls-grid">
              <div className="control-group">
                <label htmlFor="metric-select">Metric</label>
                <input
                  id="metric-select"
                  type="text"
                  value={manualMetric}
                  onChange={(e) => handleManualChange('metric', e.target.value)}
                  placeholder="Metric ID"
                />
              </div>

              <div className="control-group">
                <label htmlFor="chart-type-select">Chart Type</label>
                <select
                  id="chart-type-select"
                  value={manualChartType}
                  onChange={(e) => handleManualChange('chartType', e.target.value)}
                >
                  <option value="big_text_block">Big Text Block</option>
                  <option value="line_chart">Line Chart</option>
                  <option value="bar_chart">Bar Chart</option>
                  <option value="line_bar_chart">Line + Bar Chart</option>
                  <option value="dual_line_chart">Dual Line Chart</option>
                  <option value="triple_line_chart">Triple Line Chart</option>
                </select>
              </div>

              <div className="control-group">
                <label htmlFor="period-select">Period</label>
                <select
                  id="period-select"
                  value={manualPeriod}
                  onChange={(e) => handleManualChange('period', e.target.value)}
                >
                  <option value="Daily">Daily</option>
                  <option value="Weekly">Weekly</option>
                  <option value="Monthly">Monthly</option>
                  <option value="Quarterly">Quarterly</option>
                  <option value="Yearly">Yearly</option>
                </select>
              </div>

              <div className="control-group">
                <label htmlFor="start-date-input">Start Date</label>
                <input
                  id="start-date-input"
                  type="date"
                  value={manualStartDate}
                  onChange={(e) => handleManualChange('startDate', e.target.value)}
                />
              </div>

              <div className="control-group">
                <label htmlFor="y-label-input">Y-Axis Label</label>
                <input
                  id="y-label-input"
                  type="text"
                  value={manualYLabel}
                  onChange={(e) => handleManualChange('yLabel', e.target.value)}
                  placeholder="e.g. Gross Margin (%)"
                />
              </div>
            </div>

            {useManualConfig && (
              <button className="reset-button" onClick={handleResetToAI}>
                Reset to AI Recommendation
              </button>
            )}
          </div>
        )}

        {/* Preview */}
        <div className="preview-section">
          <h3>Preview</h3>
          <div className="preview-container">
            {renderPreview()}
          </div>
        </div>

        {/* Footer Actions */}
        <div className="modal-footer">
          <button className="cancel-button" onClick={onClose}>
            Cancel
          </button>
          <button
            className="apply-button"
            onClick={handleApplyClick}
            disabled={!chartDefinition && !recommendation}
          >
            Apply
          </button>
        </div>
      </div>
    </div>
  );
};

CardEditModal.propTypes = {
  cardId: PropTypes.string.isRequired,
  currentChart: PropTypes.object.isRequired,
  dashboardId: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  onApply: PropTypes.func.isRequired
};

export default CardEditModal;
